(() => {
  if (window.deadstockCampaignPages) return;
  window.deadstockCampaignPages = true;

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const fetchHTML = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Campaign request failed: ${response.status}`);
    return parseHTML(await response.text());
  };
  const revealCards = (root) => {
    root.querySelectorAll('.scroll-trigger').forEach((item) => {
      item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    });
  };

  class CampaignCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('.pagination a[href]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.load(new URL(link.href), true);
      });
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('page', new URLSearchParams(location.search).get('collection_page') || '1');
      this.load(url);
      this.onPopState = () => {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('page', new URLSearchParams(location.search).get('collection_page') || '1');
        this.load(url);
      };
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.removeEventListener('popstate', this.onPopState);
      this.initialized = false;
    }

    async load(url, navigate = false) {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      url.searchParams.set('view', 'campaign');
      url.searchParams.set('sort_by', 'best-selling');
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-campaign-status]');
      status.hidden = false;
      status.textContent = 'Loading best sellers…';
      try {
        const html = await fetchHTML(url, controller.signal);
        const collection = html.querySelector('collection-component');
        if (!collection?.querySelector('#product-grid')) throw new Error('Collection unavailable');
        // Assets are loaded by this section; fetched scripts must not run again.
        collection.querySelectorAll('script').forEach((script) => script.remove());
        revealCards(collection);
        const styles = [...collection.parentElement.querySelectorAll(':scope > style')];
        this.querySelector('[data-campaign-results]').replaceChildren(...styles, collection);
        status.hidden = true;
        this.querySelector('[data-campaign-fallback]').hidden = true;
        if (navigate) {
          const destination = new URL(location.href);
          destination.searchParams.set('collection_page', url.searchParams.get('page') || '1');
          history.pushState({}, '', destination);
          this.scrollIntoView({ behavior: 'auto', block: 'start' });
          const grid = this.querySelector('#product-grid');
          grid.setAttribute('tabindex', '-1');
          grid.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        status.textContent = 'Best sellers could not be loaded. Please use the collection link below.';
        this.querySelector('[data-campaign-fallback]').hidden = false;
      } finally {
        if (!controller.signal.aborted) this.setAttribute('aria-busy', 'false');
      }
    }
  }
  if (!customElements.get('campaign-collection')) customElements.define('campaign-collection', CampaignCollection);

  const initFeatured = async () => {
    const url = new URL(location.href);
    const handle = url.searchParams.get('featured');
    const collection = document.querySelector('collection-component[data-collection-handle]');
    if (!handle || !collection || document.querySelector('campaign-collection')) return;
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return;
    if (url.searchParams.get('sort_by') !== 'best-selling') {
      url.searchParams.set('sort_by', 'best-selling');
      location.replace(url);
      return;
    }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cardURL = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
      cardURL.searchParams.set('view', 'campaign-card');
      if (url.searchParams.has('preview_theme_id')) cardURL.searchParams.set('preview_theme_id', url.searchParams.get('preview_theme_id'));
      const html = await fetchHTML(cardURL);
      const card = html.querySelector('[data-campaign-card]');
      if (!card || card.dataset.productHandle !== handle) return;
      const collectionHandle = collection.dataset.collectionHandle;
      if (collectionHandle !== 'all' && !card.dataset.collections.split(' ').includes(collectionHandle)) return;
      const completed = new WeakSet();
      let applying = false;
      const apply = async () => {
        if (applying) return;
        applying = true;
        observer.disconnect();
        const current = new URL(location.href);
        const grid = collection.querySelector('ul#product-grid');
        const page = Math.max(1, Number(current.searchParams.get('page')) || 1);
        const pageSize = Number(collection.dataset.pageSize) || 16;
        // Filtered results remain authoritative: don't inject a product that may not match.
        const filtered = [...current.searchParams.keys()].some((key) => key.startsWith('filter.'));
        try {
          if (grid && !filtered && !completed.has(grid)) {
            grid.setAttribute('aria-busy', 'true');
            let cards = [...grid.children];
            if (page === 1) {
              cards = [card.cloneNode(true), ...cards.filter((item) => item.dataset.productHandle !== handle)];
            } else {
              // Moving a later product to the front shifts every intervening page by one.
              // Read a lightweight ordered handle list to see whether this page is affected.
              const offset = (page - 1) * pageSize;
              const preceding = [];
              let indexPage = 1;
              while (preceding.length < offset) {
                const indexURL = new URL(current);
                indexURL.searchParams.delete('featured');
                indexURL.searchParams.set('view', 'campaign-order');
                indexURL.searchParams.set('page', indexPage);
                const indexHTML = await fetchHTML(indexURL);
                const data = JSON.parse(indexHTML.querySelector('[data-campaign-order]').textContent);
                preceding.push(...data.handles);
                if (!data.has_next) break;
                indexPage += 1;
              }
              if (!preceding.slice(0, offset).includes(handle)) {
                const previousURL = new URL(current);
                previousURL.searchParams.delete('featured');
                previousURL.searchParams.set('page', page - 1);
                const previousHTML = await fetchHTML(previousURL);
                const previousCards = previousHTML.querySelectorAll('ul#product-grid > li');
                const carry = previousCards[previousCards.length - 1];
                if (!carry) throw new Error('Previous collection page unavailable');
                cards = [carry, ...cards].filter((item) => item.dataset.productHandle !== handle);
              }
            }
            // Keep Shopify's page boundaries full without dropping the displaced card.
            if (collection.querySelector('ul#product-grid') === grid) {
              grid.replaceChildren(...cards.slice(0, pageSize));
              revealCards(grid);
              completed.add(grid);
            }
          }
        } catch (error) {
          console.warn('Featured pagination could not be loaded.', error);
        } finally {
          grid?.setAttribute('aria-busy', 'false');
        }
        collection.querySelectorAll('.pagination a[href]').forEach((link) => {
          const target = new URL(link.href);
          target.searchParams.set('featured', handle);
          target.searchParams.set('sort_by', 'best-selling');
          link.href = target;
        });
        // Featured campaign URLs always use best sellers. Ordinary collections retain sorting.
        collection.querySelectorAll('select[name="sort_by"]').forEach((select) => {
          select.value = 'best-selling';
          select.disabled = true;
        });
        applying = false;
        observer.observe(collection, { childList: true, subtree: true });
      };
      const observer = new MutationObserver(apply);
      apply();
    } catch (error) {
      // Keep the server-rendered best-selling collection usable if the product cannot load.
      console.warn('Featured product could not be loaded.', error);
    }
  };
  initFeatured();
})();
